// NIC設定関連のハンドラー
import type { NICConfig } from '../types/index.js';
import { getNICConfig, saveNICConfig } from '../state/app-state.js';
import { t } from '../utils/i18n.js';

/**
 * NIC設定のデータを取得
 */
function getNICConfigLocal(itemId: string): NICConfig[] {
    return getNICConfig(itemId);
}

/**
 * NIC設定のデータを保存
 */
function saveNICConfigLocal(itemId: string, nics: NICConfig[]): void {
    saveNICConfig(itemId, nics);
}

/**
 * NIC設定のHTMLを生成
 */
function createNICItemHTML(itemId: string, index: number, nic: NICConfig | null = null): string {
    const adapterName = nic?.adapterName || t('nicAdapterName');
    const configType = nic?.configType || 'static';
    const ipAddress = nic?.ipAddress || t('nicIPAddress');
    const subnetMask = nic?.subnetMask || t('nicSubnetMask');
    const gateway = nic?.gateway || '';
    const dnsPrimary = nic?.dnsPrimary || '';
    const dnsSecondary = nic?.dnsSecondary || '';
    
    return `
        <div class="p-3 bg-gray-900/50 rounded border border-gray-700" data-nic-index="${index}">
            <div class="flex items-center justify-between mb-2">
                <h4 class="text-xs font-bold text-gray-300">NIC #${index + 1}</h4>
                <button 
                    type="button"
                    onclick="window.app.removeNIC('${itemId}', ${index})"
                    class="px-2 py-1 text-[10px] bg-red-600 hover:bg-red-700 text-white rounded transition">
                    ${t('nicDelete')}
                </button>
            </div>
            <div class="space-y-2">
                <div>
                    <label class="block text-[10px] text-gray-400 mb-1">${t('nicAdapterNameLabel')}</label>
                    <input 
                        type="text"
                        class="nic-adapter-name w-full px-2 py-1 text-xs bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                        placeholder="${t('nicAdapterName')}"
                        value="${adapterName}"
                        data-item-id="${itemId}"
                        data-nic-index="${index}"
                        oninput="window.app.updateNICConfig('${itemId}', ${index})"
                    />
                </div>
                <div>
                    <label class="block text-[10px] text-gray-400 mb-1">${t('nicConfigTypeLabel')}</label>
                    <select 
                        class="nic-config-type w-full px-2 py-1 text-xs bg-gray-800 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
                        data-item-id="${itemId}"
                        data-nic-index="${index}"
                        onchange="window.app.updateNICConfig('${itemId}', ${index})"
                    >
                        <option value="static" ${configType === 'static' ? 'selected' : ''}>${t('nicStaticIP')}</option>
                        <option value="dhcp" ${configType === 'dhcp' ? 'selected' : ''}>${t('nicDHCP')}</option>
                    </select>
                </div>
                <div class="nic-static-config" style="display: ${configType === 'static' ? 'block' : 'none'};">
                    <div class="grid grid-cols-2 gap-2">
                        <div>
                            <label class="block text-[10px] text-gray-400 mb-1">${t('nicIPAddressLabel')}</label>
                            <input 
                                type="text"
                                class="nic-ip-address w-full px-2 py-1 text-xs bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                placeholder="${t('nicIPAddress')}"
                                value="${ipAddress}"
                                data-item-id="${itemId}"
                                data-nic-index="${index}"
                                autocomplete="off"
                                oninput="window.app.updateNICConfig('${itemId}', ${index}); window.app.updateGatewayPlaceholder('${itemId}', ${index})"
                            />
                        </div>
                        <div>
                            <label class="block text-[10px] text-gray-400 mb-1">${t('nicSubnetMaskLabel')}</label>
                            <input 
                                type="text"
                                class="nic-subnet-mask w-full px-2 py-1 text-xs bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                placeholder="${t('nicSubnetMask')}"
                                value="${subnetMask}"
                                data-item-id="${itemId}"
                                data-nic-index="${index}"
                                oninput="window.app.updateNICConfig('${itemId}', ${index})"
                            />
                        </div>
                    </div>
                    <div class="mt-2">
                        <label class="block text-[10px] text-gray-400 mb-1">${t('nicGatewayLabel')}</label>
                        <div class="relative">
                            <input 
                                type="text"
                                id="nic-gateway-${itemId}-${index}"
                                class="nic-gateway w-full px-2 py-1 text-xs bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                placeholder="${t('nicGateway')}"
                                value="${gateway}"
                                data-item-id="${itemId}"
                                data-nic-index="${index}"
                                autocomplete="off"
                                oninput="window.app.updateNICConfig('${itemId}', ${index}); window.app.hideGatewayAutocomplete('${itemId}', ${index})"
                                onkeydown="window.app.handleGatewayKeyDown('${itemId}', ${index}, event)"
                                onfocus="window.app.showGatewayAutocomplete('${itemId}', ${index})"
                                onblur="window.app.hideGatewayAutocomplete('${itemId}', ${index})"
                            />
                            <div id="gateway-autocomplete-${itemId}-${index}" class="hidden absolute z-10 w-full mt-1 bg-gray-800 border border-gray-600 rounded shadow-lg">
                                <div class="px-2 py-1 text-xs text-gray-400 border-b border-gray-700">${t('nicAutocompleteLabel')}</div>
                                <div class="px-2 py-1 text-xs text-blue-300 cursor-pointer hover:bg-gray-700" onclick="window.app.applyGatewayAutocomplete('${itemId}', ${index}, this.textContent)"></div>
                            </div>
                        </div>
                    </div>
                    <div class="mt-2">
                        <label class="block text-[10px] text-gray-400 mb-1">${t('nicDNSLabel')}</label>
                        <div class="grid grid-cols-2 gap-2">
                            <div>
                                <label class="block text-[10px] text-gray-400 mb-1">${t('nicDNSPrimaryLabel')}</label>
                                <input 
                                    type="text"
                                    class="nic-dns-primary w-full px-2 py-1 text-xs bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                    placeholder="${t('nicDNSPrimary')}"
                                    value="${dnsPrimary}"
                                    data-item-id="${itemId}"
                                    data-nic-index="${index}"
                                    oninput="window.app.updateNICConfig('${itemId}', ${index})"
                                />
                            </div>
                            <div>
                                <label class="block text-[10px] text-gray-400 mb-1">${t('nicDNSSecondaryLabel')}</label>
                                <input 
                                    type="text"
                                    class="nic-dns-secondary w-full px-2 py-1 text-xs bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                    placeholder="${t('nicDNSSecondary')}"
                                    value="${dnsSecondary}"
                                    data-item-id="${itemId}"
                                    data-nic-index="${index}"
                                    oninput="window.app.updateNICConfig('${itemId}', ${index})"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
}

/**
 * NIC設定のコードプレビューを更新
 */
function updateNICCodePreview(itemId: string): void {
    const nics = getNICConfigLocal(itemId);
    const codePreviewDiv = document.getElementById(`codePreview_${itemId}`);
    if (!codePreviewDiv) return;
    
    if (nics.length === 0) {
        codePreviewDiv.classList.add('hidden');
        return;
    }
    
    let code = '';
    nics.forEach((nic, index) => {
        code += `:: NIC #${index + 1} (${nic.adapterName})\n`;
        if (nic.configType === 'static') {
            // ゲートウェイが空の場合はゲートウェイなしで設定
            if (nic.gateway && nic.gateway.trim()) {
                code += `netsh interface ip set address "${nic.adapterName}" static ${nic.ipAddress} ${nic.subnetMask} ${nic.gateway} 1\n`;
            } else {
                code += `netsh interface ip set address "${nic.adapterName}" static ${nic.ipAddress} ${nic.subnetMask}\n`;
            }
            // DNSが空でない場合のみ設定
            if (nic.dnsPrimary && nic.dnsPrimary.trim()) {
                code += `netsh interface ip set dns "${nic.adapterName}" static ${nic.dnsPrimary}\n`;
                if (nic.dnsSecondary && nic.dnsSecondary.trim()) {
                    code += `netsh interface ip add dns "${nic.adapterName}" ${nic.dnsSecondary} index=2\n`;
                }
            }
        } else {
            code += `netsh interface ip set dns "${nic.adapterName}" dhcp\n`;
            code += `netsh interface ip set address "${nic.adapterName}" dhcp\n`;
        }
        code += '\n';
    });
    
    const safeCode = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    codePreviewDiv.innerHTML = `
        <span class="text-gray-500 pr-1">bat&gt;</span>
        <pre class="inline whitespace-pre-wrap align-middle">${safeCode}</pre>
    `;
    codePreviewDiv.classList.remove('hidden');
}

/**
 * NIC設定リストを再描画
 */
export function renderNICList(itemId: string): void {
    const listContainer = document.getElementById(`nicList_${itemId}`);
    if (!listContainer) return;
    
    const nics = getNICConfigLocal(itemId);
    
    if (nics.length === 0) {
        listContainer.innerHTML = `<p class="text-xs text-gray-500 text-center py-2">${t('nicNotConfigured')}</p>`;
        return;
    }
    
    listContainer.innerHTML = nics.map((nic, index) => createNICItemHTML(itemId, index, nic)).join('');
    
    // 設定タイプに応じて表示/非表示を切り替え
    nics.forEach((_nic, index) => {
        const staticConfig = listContainer.querySelector(`[data-nic-index="${index}"] .nic-static-config`) as HTMLElement | null;
        const configTypeSelect = listContainer.querySelector(`[data-nic-index="${index}"] .nic-config-type`) as HTMLSelectElement | null;
        if (staticConfig && configTypeSelect) {
            staticConfig.style.display = configTypeSelect.value === 'static' ? 'block' : 'none';
        }
    });
    
    // コードプレビューを更新
    updateNICCodePreview(itemId);
}

/**
 * IPアドレスからゲートウェイの補完候補を取得
 */
function getGatewayAutocomplete(_itemId: string, index: number): string | null {
    const nicContainer = document.querySelector(`[data-nic-index="${index}"]`);
    if (!nicContainer) return null;
    
    const ipAddressInput = nicContainer.querySelector('.nic-ip-address') as HTMLInputElement | null;
    if (!ipAddressInput) return null;
    
    const ipAddress = ipAddressInput.value.trim();
    if (!ipAddress) return null;
    
    // IPアドレスの最初の3オクテットからゲートウェイを推測
    const ipParts = ipAddress.split('.');
    if (ipParts.length >= 3) {
        return `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.1`;
    }
    
    return null;
}

/**
 * NICを追加
 */
export function addNIC(itemId: string): void {
    const nics = getNICConfigLocal(itemId);
    nics.push({
        adapterName: t('nicAdapterName'),
        configType: 'static',
        ipAddress: t('nicIPAddress'),
        subnetMask: '255.255.255.0',
        gateway: '',
        dnsPrimary: '',
        dnsSecondary: ''
    });
    saveNICConfigLocal(itemId, nics);
    renderNICList(itemId);
    // updateCodeはwindow.app経由で呼び出す（循環参照を避けるため）
    if (window.app && window.app.updateCode) {
        window.app.updateCode(false);
    }
}

/**
 * NICを削除
 */
export function removeNIC(itemId: string, index: number): void {
    const nics = getNICConfigLocal(itemId);
    nics.splice(index, 1);
    saveNICConfigLocal(itemId, nics);
    renderNICList(itemId);
    // updateCodeはwindow.app経由で呼び出す（循環参照を避けるため）
    if (window.app && window.app.updateCode) {
        window.app.updateCode(false);
    }
}

/**
 * ゲートウェイのオートコンプリートを表示
 */
export function showGatewayAutocomplete(itemId: string, index: number): void {
    const autocompleteDiv = document.getElementById(`gateway-autocomplete-${itemId}-${index}`);
    if (!autocompleteDiv) return;
    
    const gatewayInput = document.getElementById(`nic-gateway-${itemId}-${index}`) as HTMLInputElement | null;
    if (!gatewayInput) return;
    
    // ゲートウェイが既に入力されている場合は表示しない
    if (gatewayInput.value.trim()) {
        autocompleteDiv.classList.add('hidden');
        return;
    }
    
    const suggested = getGatewayAutocomplete(itemId, index);
    const suggestionDiv = autocompleteDiv.querySelector('div:last-child');
    
    if (suggested && suggestionDiv) {
        suggestionDiv.textContent = suggested;
        autocompleteDiv.classList.remove('hidden');
    } else {
        autocompleteDiv.classList.add('hidden');
    }
}

/**
 * ゲートウェイのオートコンプリートを非表示
 */
export function hideGatewayAutocomplete(itemId: string, index: number): void {
    // 少し遅延させて、クリックイベントが発火する前に非表示にしない
    setTimeout(() => {
        const autocompleteDiv = document.getElementById(`gateway-autocomplete-${itemId}-${index}`);
        if (autocompleteDiv) {
            autocompleteDiv.classList.add('hidden');
        }
    }, 200);
}

/**
 * ゲートウェイのオートコンプリートを適用
 */
export function applyGatewayAutocomplete(itemId: string, index: number, value: string): void {
    const gatewayInput = document.getElementById(`nic-gateway-${itemId}-${index}`) as HTMLInputElement | null;
    if (gatewayInput) {
        gatewayInput.value = value;
        updateNICConfig(itemId, index);
        gatewayInput.focus();
    }
}

/**
 * ゲートウェイ入力のキーダウン処理
 */
export function handleGatewayKeyDown(itemId: string, index: number, event: KeyboardEvent): void {
    const autocompleteDiv = document.getElementById(`gateway-autocomplete-${itemId}-${index}`);
    const input = event.target as HTMLInputElement | null;
    if (!autocompleteDiv || !input) return;
    
    // Tabキーまたは右矢印キーで補完を確定
    if ((event.key === 'Tab' || event.key === 'ArrowRight') && !autocompleteDiv.classList.contains('hidden')) {
        const suggestionDiv = autocompleteDiv.querySelector('div:last-child');
        const suggested = suggestionDiv?.textContent;
        if (suggested) {
            event.preventDefault();
            input.value = suggested;
            updateNICConfig(itemId, index);
            input.setSelectionRange(suggested.length, suggested.length);
        }
    }
    // Escapeキーで補完を非表示
    else if (event.key === 'Escape') {
        autocompleteDiv.classList.add('hidden');
    }
}

/**
 * IPアドレスからゲートウェイのプレースホルダーを更新
 */
export function updateGatewayPlaceholder(_itemId: string, index: number): void {
    const nicContainer = document.querySelector(`[data-nic-index="${index}"]`);
    if (!nicContainer) return;
    
    const ipAddressInput = nicContainer.querySelector('.nic-ip-address') as HTMLInputElement | null;
    const gatewayInput = nicContainer.querySelector('.nic-gateway') as HTMLInputElement | null;
    
    if (!ipAddressInput || !gatewayInput) return;
    
    const ipAddress = ipAddressInput.value.trim();
    
    // IPアドレスが空でない場合、最初の3オクテットからゲートウェイを推測
    if (ipAddress) {
        const ipParts = ipAddress.split('.');
        if (ipParts.length >= 3) {
            const suggestedGateway = `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.1`;
            const optionalText = t('nicGateway').includes('optional') ? ' (optional)' : '（空欄可）';
            gatewayInput.placeholder = `${suggestedGateway}${optionalText}`;
        }
    } else {
        gatewayInput.placeholder = t('nicGateway');
    }
}

/**
 * NIC設定を更新
 */
export function updateNICConfig(itemId: string, index: number): void {
    const nics = getNICConfigLocal(itemId);
    if (!nics[index]) return;
    
    const nicContainer = document.querySelector(`[data-nic-index="${index}"]`);
    if (!nicContainer) return;
    
    const adapterNameInput = nicContainer.querySelector('.nic-adapter-name') as HTMLInputElement | null;
    const configTypeSelect = nicContainer.querySelector('.nic-config-type') as HTMLSelectElement | null;
    const ipAddressInput = nicContainer.querySelector('.nic-ip-address') as HTMLInputElement | null;
    const subnetMaskInput = nicContainer.querySelector('.nic-subnet-mask') as HTMLInputElement | null;
    const gatewayInput = nicContainer.querySelector('.nic-gateway') as HTMLInputElement | null;
    const dnsPrimaryInput = nicContainer.querySelector('.nic-dns-primary') as HTMLInputElement | null;
    const dnsSecondaryInput = nicContainer.querySelector('.nic-dns-secondary') as HTMLInputElement | null;
    
    nics[index] = {
        adapterName: adapterNameInput?.value || t('nicAdapterName'),
        configType: (configTypeSelect?.value as 'static' | 'dhcp') || 'static',
        ipAddress: ipAddressInput?.value || t('nicIPAddress'),
        subnetMask: subnetMaskInput?.value || '255.255.255.0',
        gateway: gatewayInput?.value?.trim() || '',
        dnsPrimary: dnsPrimaryInput?.value?.trim() || '',
        dnsSecondary: dnsSecondaryInput?.value?.trim() || ''
    };
    
    saveNICConfigLocal(itemId, nics);
    
    // 設定タイプに応じて表示/非表示を切り替え
    const staticConfig = nicContainer.querySelector('.nic-static-config') as HTMLElement | null;
    if (staticConfig && configTypeSelect) {
        staticConfig.style.display = configTypeSelect.value === 'static' ? 'block' : 'none';
    }
    
    updateNICCodePreview(itemId);
    // updateCodeはwindow.app経由で呼び出す（循環参照を避けるため）
    if (window.app && window.app.updateCode) {
        window.app.updateCode(false);
    }
}

